import { ZuruflexApiError } from '@zuruflex/api-client';
import { formatDateIn, formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import {
  PARTNER_KINDS,
  type DocumentRequirement,
  type ListingStatus,
  type Partner,
  type PartnerKind,
  type PartnerDocument,
  type PartnerTerms,
  type TermsStatus,
} from '@zuruflex/types';
import { formatPhone } from '@zuruflex/utils';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  TermsDocument,
  Textarea,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ExternalLink, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { AppVersion } from '../components/AppVersion.js';
import { CarOwnersCard } from '../components/CarOwnersCard.js';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const STATUS_VARIANT = {
  APPROVED: 'success',
  PENDING: 'warning',
  SUSPENDED: 'danger',
  CLOSED: 'default',
} as const;

const KYC_VARIANT = {
  VERIFIED: 'success',
  PENDING: 'warning',
  REJECTED: 'danger',
  UNVERIFIED: 'outline',
} as const;

const TERMS_VARIANT: Record<TermsStatus, 'default' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  IN_REVIEW: 'info',
  PUBLISHED: 'success',
  REJECTED: 'warning',
};

const DOCUMENT_VARIANT = {
  MISSING: 'outline',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'danger',
} as const;

/**
 * Where a partner is actually reviewed.
 *
 * The approve button used to sit on the table, which made approving one a
 * decision taken without looking at anything. Here the paperwork is on the
 * same screen as the decision, and the decision is refused until the
 * paperwork has been gone through — the API enforces that, and this page
 * says so before the button is pressed rather than after.
 */
export function PartnerDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);

  const partner = useQuery({ queryKey: ['partner', id], queryFn: () => api.getPartner(id) });
  const onboarding = useQuery({
    queryKey: ['partner-onboarding', id],
    queryFn: () => api.partnerOnboarding(id),
  });
  const documents = useQuery({
    queryKey: ['partner-documents', id],
    queryFn: () => api.partnerDocuments(id),
  });
  const team = useQuery({ queryKey: ['partner-team', id], queryFn: () => api.partnerTeam(id) });

  if (partner.error) {
    return <ErrorState error={partner.error} onRetry={() => void partner.refetch()} />;
  }
  if (partner.isPending) return <Skeleton className="h-96 w-full" />;

  const record = partner.data;

  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to="/partners"
            className="inline-flex items-center gap-1 text-sm text-text-muted transition-colors hover:text-text"
          >
            <ChevronLeft className="size-4" />
            {t('partners.backToPartners')}
          </Link>
        }
        title={record.displayName}
        description={`${t(`common:partnerKind.${record.kind}`)} · ${record.slug}`}
        action={
          <div className="flex items-center gap-2">
            {record.isFirstParty ? (
              <Badge variant="outline">{t('partners.firstParty')}</Badge>
            ) : null}
            <Badge variant={KYC_VARIANT[record.kycStatus]}>
              {t(`common:kycStatus.${record.kycStatus}`)}
            </Badge>
            <Badge variant={STATUS_VARIANT[record.status]}>
              {t(`common:partnerStatus.${record.status}`)}
            </Badge>
          </div>
        }
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>{t('partners.paperwork')}</CardTitle>
                <CardDescription>{t('partners.paperworkHint')}</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {onboarding.isPending ? (
                <Skeleton className="h-28 w-full" />
              ) : (
                <ul className="flex flex-col gap-2">
                  {(onboarding.data?.documents ?? []).map((requirement) => (
                    <DocumentReviewRow
                      key={requirement.kind}
                      partnerId={id}
                      requirement={requirement}
                    />
                  ))}
                </ul>
              )}

              {/* Anything filed beyond what was asked for — a proof of
                  address. It still needs a decision, so it cannot only be
                  visible on the partner's own screen. A car's paperwork is
                  not here: it is decided with that car's listing. */}
              <OtherDocuments
                partnerId={id}
                documents={documents.data ?? []}
                required={onboarding.data?.documents ?? []}
              />
            </CardContent>
          </Card>

          <CarsCard partnerId={id} />

          <TermsCard partnerId={id} />

          <ParticularsCard partner={record} />

          <Card>
            <CardHeader>
              <div>
                <CardTitle>{t('partners.team')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="flex flex-col gap-2">
                {(team.data ?? []).map((member) => (
                  <li
                    key={member.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-field border border-border-default px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{member.fullName}</p>
                      <p className="truncate text-xs text-text-muted">{member.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {member.app ? <AppVersion app={member.app} /> : null}
                      {member.lastLoginAt ? (
                        <span className="text-xs text-text-muted">
                          {formatDateIn(member.lastLoginAt, locale)}
                        </span>
                      ) : null}
                      <Badge>{t(`common:partnerRole.${member.role}`)}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          {record.isFirstParty ? <CarOwnersCard partnerId={id} /> : null}
        </div>

        <div className="space-y-5">
          <ChecklistCard partnerId={id} />
          {/* Keyed so the commission it prefills follows an edit made above. */}
          <DecisionCard key={record.commissionBps} partner={record} />
        </div>
      </div>
    </>
  );
}

// ── Particulars, and correcting them ─────────────────────────

type ParticularsForm = {
  displayName: string;
  kind: PartnerKind;
  legalName: string;
  registrationNumber: string;
  taxId: string;
  contactPhone: string;
  contactEmail: string;
  commissionPercent: string;
};

function formOf(partner: Partner): ParticularsForm {
  return {
    displayName: partner.displayName,
    kind: partner.kind,
    legalName: partner.legalName ?? '',
    registrationNumber: partner.registrationNumber ?? '',
    taxId: partner.taxId ?? '',
    contactPhone: partner.contactPhone,
    contactEmail: partner.contactEmail ?? '',
    commissionPercent: String(partner.commissionBps / 100),
  };
}

/**
 * Read-only until a manager asks to edit. Only what changed is sent, so a
 * save cannot quietly rewrite a field somebody else corrected meanwhile,
 * and a blank optional field is left as it was rather than refused.
 */
function ParticularsCard({ partner }: { partner: Partner }) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === 'OWNER' || user?.role === 'MANAGER';

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ParticularsForm>(() => formOf(partner));

  const set = <K extends keyof ParticularsForm>(key: K, value: ParticularsForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const commissionBps = Math.round(Number(form.commissionPercent) * 100);
  const commissionValid =
    form.commissionPercent.trim() !== '' &&
    Number.isFinite(commissionBps) &&
    commissionBps >= 0 &&
    commissionBps <= 5000;

  const save = useMutation({
    mutationFn: () => {
      const before = formOf(partner);
      const changed: Record<string, unknown> = {};
      for (const key of [
        'displayName',
        'legalName',
        'registrationNumber',
        'taxId',
        'contactPhone',
        'contactEmail',
      ] as const) {
        const value = form[key].trim();
        if (value && value !== before[key]) changed[key] = value;
      }
      if (form.kind !== partner.kind) changed.kind = form.kind;
      if (commissionBps !== partner.commissionBps) changed.commissionBps = commissionBps;
      if (Object.keys(changed).length === 0) return Promise.resolve(partner);
      return api.updatePartner(partner.id, changed);
    },
    onSuccess: async (next) => {
      // Seeded from the response so reopening the form straight away starts
      // from what was saved, not from the record the refetch has yet to replace.
      queryClient.setQueryData(['partner', partner.id], next);
      setEditing(false);
      // A change of kind changes what paperwork is required.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['partner', partner.id] }),
        queryClient.invalidateQueries({ queryKey: ['partner-onboarding', partner.id] }),
        queryClient.invalidateQueries({ queryKey: ['partners'] }),
      ]);
    },
  });

  const startEditing = () => {
    setForm(formOf(partner));
    save.reset();
    setEditing(true);
  };

  const particulars = [
    [t('partners.legalName'), partner.legalName ?? '—'],
    [t('partners.registrationNumber'), partner.registrationNumber ?? '—'],
    [t('partners.taxId'), partner.taxId ?? '—'],
    [t('common:field.phone'), formatPhone(partner.contactPhone)],
    [t('common:field.email'), partner.contactEmail ?? '—'],
    [t('partners.country'), `${partner.country} · ${partner.currency}`],
    [t('common:field.commission'), `${(partner.commissionBps / 100).toFixed(2)}%`],
    [t('common:field.joined'), formatDateTimeIn(partner.createdAt, locale)],
    [
      t('partners.submitted'),
      partner.submittedAt ? formatDateTimeIn(partner.submittedAt, locale) : t('partners.notYet'),
    ],
  ] as const;

  const text = (key: Exclude<keyof ParticularsForm, 'kind'>, label: string, type = 'text') => (
    <FormField label={label}>
      {(field) => (
        <Input
          {...field}
          type={type}
          value={form[key]}
          onChange={(event) => set(key, event.target.value)}
        />
      )}
    </FormField>
  );

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('partners.particulars')}</CardTitle>
        </div>
        {canEdit && !editing ? (
          <Button size="sm" variant="ghost" onClick={startEditing}>
            <Pencil className="size-3.5" />
            <span className="ml-1">{t('partners.edit')}</span>
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {editing ? (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            {save.error ? (
              <Alert variant="danger">
                {save.error instanceof ZuruflexApiError
                  ? save.error.message
                  : t('common:error.unexpected')}
              </Alert>
            ) : null}

            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              {text('displayName', t('common:field.name'))}
              <FormField label={t('partners.kind')} hint={t('partners.kindHint')}>
                {(field) => (
                  <Select
                    value={form.kind}
                    onValueChange={(value) => set('kind', value as PartnerKind)}
                  >
                    <SelectTrigger id={field.id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PARTNER_KINDS.map((kind) => (
                        <SelectItem key={kind} value={kind}>
                          {t(`common:partnerKind.${kind}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormField>
              {text('legalName', t('partners.legalName'))}
              {text('registrationNumber', t('partners.registrationNumber'))}
              {text('taxId', t('partners.taxId'))}
              {text('contactPhone', t('common:field.phone'), 'tel')}
              {text('contactEmail', t('common:field.email'), 'email')}
              <FormField
                label={t('partners.commissionPercent')}
                error={commissionValid ? undefined : t('partners.commissionRange')}
              >
                {(field) => (
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={50}
                    step={0.01}
                    value={form.commissionPercent}
                    onChange={(event) => set('commissionPercent', event.target.value)}
                  />
                )}
              </FormField>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={!commissionValid || !form.displayName.trim() || save.isPending}
              >
                {t('common:action.save')}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
                {t('common:action.cancel')}
              </Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {particulars.map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-text-muted">{label}</dt>
                <dd className="text-sm">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

// ── Their cars ───────────────────────────────────────────────

const LISTING_VARIANT: Record<
  ListingStatus,
  'success' | 'info' | 'danger' | 'default' | 'outline'
> = {
  PUBLISHED: 'success',
  IN_REVIEW: 'info',
  REJECTED: 'danger',
  DRAFT: 'default',
  UNLISTED: 'outline',
};

/**
 * Each car and how far its listing has got. A car — paperwork and all — is
 * reviewed on its own page, so this is the way there rather than a second
 * place to decide it.
 */
function CarsCard({ partnerId }: { partnerId: string }) {
  const { t } = useTranslation(['admin', 'common']);
  const { data, isPending } = useQuery({
    queryKey: ['fleet', { partnerId }],
    queryFn: () => api.listVehicles({ partnerId, perPage: 100 }),
  });

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('partners.cars')}</CardTitle>
          <CardDescription>{t('partners.carsHint')}</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <Skeleton className="h-20 w-full" />
        ) : !data?.items.length ? (
          <p className="text-sm text-text-muted">{t('partners.noCars')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.items.map((car) => (
              <li key={car.id}>
                <Link
                  to={`/fleet/${car.slug}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-field border border-border-default px-3 py-2 transition-colors hover:bg-surface-sunken"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {car.year} {car.make} {car.model}
                    </p>
                    <p className="truncate font-mono text-xs text-text-muted">{car.plateNumber}</p>
                  </div>
                  <Badge variant={LISTING_VARIANT[car.listingStatus]}>
                    {t(`common:listingStatus.${car.listingStatus}`)}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ── The partner's own rental terms ───────────────────────────

/**
 * What this partner asks their renters to agree to, and Zuruflex's call on it.
 *
 * Built like the document rows above rather than as its own page: the
 * decision is "does this belong in front of a customer", and that is a
 * question you answer while looking at the rest of the account, not on a
 * screen you had to navigate to.
 */
function TermsCard({ partnerId }: { partnerId: string }) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const queryClient = useQueryClient();

  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const terms = useQuery({
    queryKey: ['partner-terms', partnerId],
    queryFn: () => api.partnerTerms(partnerId),
  });

  const pending = terms.data?.working?.status === 'IN_REVIEW' ? terms.data.working : null;
  // What renters are agreeing to today, which is not the same row as the
  // one awaiting a decision — and the reviewer needs to see both.
  const live = terms.data?.current ?? null;

  const decide = useMutation({
    mutationFn: (status: 'PUBLISHED' | 'REJECTED') => {
      if (!pending) throw new Error('nothing to decide on');
      return api.reviewPartnerTerms(partnerId, pending.id, {
        status,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
    },
    onSuccess: async () => {
      setRejecting(false);
      setNote('');
      setError(null);
      // Publishing moves the partner's live pointer, so the record moves
      // with the terms — and the queue outside loses its marker.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['partner-terms', partnerId] }),
        queryClient.invalidateQueries({ queryKey: ['partner', partnerId] }),
        queryClient.invalidateQueries({ queryKey: ['partners'] }),
      ]);
    },
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
  });

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('partners.terms')}</CardTitle>
          <CardDescription>{t('partners.termsHint')}</CardDescription>
        </div>
        {pending ? (
          <Badge variant={TERMS_VARIANT.IN_REVIEW}>{t('partners.termsWaiting')}</Badge>
        ) : null}
      </CardHeader>
      <CardContent>
        {terms.isPending ? <Skeleton className="h-24 w-full" /> : null}

        {!terms.isPending && !pending && !live ? (
          <p className="text-sm text-text-muted">{t('partners.termsNone')}</p>
        ) : null}

        {pending ? (
          <div className="rounded-field border border-border-default p-4">
            <p className="text-xs text-text-muted">
              {t('partners.termsSubmitted', {
                date: pending.submittedAt ? formatDateTimeIn(pending.submittedAt, locale) : '—',
              })}
            </p>
            <div className="mt-4 max-h-96 overflow-y-auto">
              <TermsDocument doc={pending.doc} />
            </div>

            {error ? (
              <Alert variant="danger" className="mt-4">
                {error}
              </Alert>
            ) : null}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={decide.isPending}
                onClick={() => decide.mutate('PUBLISHED')}
              >
                {t('partners.termsPublish')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRejecting((open) => !open)}>
                {t('partners.termsSendBack')}
              </Button>
            </div>

            {rejecting ? (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <FormField label={t('partners.termsNote')} className="min-w-64 flex-1">
                  {(field) => (
                    <Textarea
                      {...field}
                      rows={2}
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder={t('partners.termsNotePlaceholder')}
                    />
                  )}
                </FormField>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={note.trim().length < 3 || decide.isPending}
                  onClick={() => decide.mutate('REJECTED')}
                >
                  {t('partners.termsSendBack')}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {live ? <LiveTerms terms={live} locale={locale} boxed={Boolean(pending)} /> : null}
      </CardContent>
    </Card>
  );
}

function LiveTerms({
  terms,
  locale,
  boxed,
}: {
  terms: PartnerTerms;
  locale: ReturnType<typeof toLocale>;
  boxed: boolean;
}) {
  const { t } = useTranslation('admin');
  return (
    <details className={boxed ? 'mt-4' : undefined}>
      <summary className="cursor-pointer text-sm font-medium">
        {t('partners.termsLive', {
          version: terms.version,
          date: terms.publishedAt ? formatDateIn(terms.publishedAt, locale) : '—',
        })}
      </summary>
      <div className="mt-3 max-h-96 overflow-y-auto rounded-field border border-border-default p-4">
        <TermsDocument doc={terms.doc} />
      </div>
    </details>
  );
}

// ── The checklist, as the partner sees it ────────────────────

function ChecklistCard({ partnerId }: { partnerId: string }) {
  const { t } = useTranslation(['admin', 'common']);
  const { data, isPending } = useQuery({
    queryKey: ['partner-onboarding', partnerId],
    queryFn: () => api.partnerOnboarding(partnerId),
  });

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('partners.checklist')}</CardTitle>
          <CardDescription>{t('partners.checklistHint')}</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        {isPending || !data ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <ul className="flex flex-col gap-2">
            {data.steps.map((step) => (
              <li key={step.step} className="flex items-center justify-between gap-3">
                <span className="text-sm">
                  {t(`common:onboardingStep.${step.step}`)}
                  {step.required ? null : (
                    <span className="ml-1 text-xs text-text-muted">
                      ({t('partners.optionalStep')})
                    </span>
                  )}
                </span>
                <Badge variant={step.done ? 'success' : 'outline'}>
                  {t(step.done ? 'partners.stepDone' : 'partners.stepOutstanding')}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ── One document, and the decision on it ─────────────────────

function DocumentReviewRow({
  partnerId,
  requirement,
}: {
  partnerId: string;
  requirement: DocumentRequirement<PartnerDocument>;
}) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const { kind, state, document } = requirement;

  const decide = useMutation({
    mutationFn: (status: 'APPROVED' | 'REJECTED') => {
      if (!document) throw new Error('nothing to decide on');
      return api.reviewPartnerDocument(partnerId, document.id, {
        status,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
    },
    onSuccess: async () => {
      setRejecting(false);
      setNote('');
      // Approving the last outstanding document verifies the partner, so
      // the record and the checklist move with the document.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['partner', partnerId] }),
        queryClient.invalidateQueries({ queryKey: ['partner-onboarding', partnerId] }),
        queryClient.invalidateQueries({ queryKey: ['partner-documents', partnerId] }),
        queryClient.invalidateQueries({ queryKey: ['partners'] }),
      ]);
    },
  });

  return (
    <li className="rounded-field border border-border-default px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{t(`common:documentKind.${kind}`)}</p>
          <p className="truncate text-xs text-text-muted">
            {document
              ? [
                  document.number,
                  document.expiresAt
                    ? t('partners.expires', { date: formatDateIn(document.expiresAt, locale) })
                    : null,
                  document.reviewedBy
                    ? t('partners.reviewedBy', { name: document.reviewedBy })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || t('partners.noDetails')
              : t('partners.neverFiled')}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Badge variant={DOCUMENT_VARIANT[state]}>{t(`common:documentState.${state}`)}</Badge>
          {document ? (
            <Button asChild size="sm" variant="ghost">
              <a href={document.url} target="_blank" rel="noreferrer">
                <ExternalLink className="size-3.5" />
                <span className="ml-1">{t('partners.view')}</span>
              </a>
            </Button>
          ) : null}
        </div>
      </div>

      {document && document.status !== 'APPROVED' ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate('APPROVED')}>
            {t('partners.approveDocument')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRejecting((open) => !open)}>
            {t('partners.rejectDocument')}
          </Button>
        </div>
      ) : null}

      {rejecting ? (
        <div className="mt-3">
          <FormField
            label={t('partners.rejectNote')}
            required
            error={decide.error ? (decide.error as Error).message : undefined}
          >
            {(field) => (
              <Textarea
                {...field}
                rows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </FormField>
          <Button
            size="sm"
            variant="destructive"
            className="mt-2"
            disabled={!note.trim() || decide.isPending}
            onClick={() => decide.mutate('REJECTED')}
          >
            {t('partners.sendBack')}
          </Button>
        </div>
      ) : null}

      {document?.reviewNote && document.status === 'REJECTED' ? (
        <p className="mt-2 text-xs text-danger">{document.reviewNote}</p>
      ) : null}
    </li>
  );
}

/** Everything filed that was not on the required list. */
function OtherDocuments({
  partnerId,
  documents,
  required,
}: {
  partnerId: string;
  documents: PartnerDocument[];
  required: DocumentRequirement<PartnerDocument>[];
}) {
  const { t } = useTranslation(['admin', 'common']);
  const accountedFor = new Set(required.map((each) => each.document?.id).filter(Boolean));
  const others = documents.filter(
    (document) => !document.vehicleId && !accountedFor.has(document.id),
  );

  if (others.length === 0) return null;

  return (
    <>
      <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('partners.alsoFiled')}
      </p>
      <ul className="mt-2 flex flex-col gap-2">
        {others.map((document) => (
          <DocumentReviewRow
            key={document.id}
            partnerId={partnerId}
            requirement={{
              kind: document.kind,
              state: document.isExpired
                ? 'EXPIRED'
                : (document.status as 'PENDING' | 'APPROVED' | 'REJECTED'),
              document,
            }}
          />
        ))}
      </ul>
    </>
  );
}

// ── Approve, suspend, close ──────────────────────────────────

function DecisionCard({ partner }: { partner: Partner }) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();

  const [reason, setReason] = useState('');
  const [commissionBps, setCommissionBps] = useState(String(partner.commissionBps));

  const decide = useMutation({
    mutationFn: (status: 'APPROVED' | 'SUSPENDED' | 'CLOSED') =>
      api.reviewPartner(partner.id, {
        status,
        reason: reason.trim() || undefined,
        ...(status === 'APPROVED' && commissionBps ? { commissionBps: Number(commissionBps) } : {}),
      }),
    onSuccess: async () => {
      setReason('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['partner', partner.id] }),
        queryClient.invalidateQueries({ queryKey: ['partner-onboarding', partner.id] }),
        queryClient.invalidateQueries({ queryKey: ['partners'] }),
      ]);
    },
  });

  if (partner.isFirstParty) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-text-muted">{t('partners.firstPartyNote')}</p>
        </CardContent>
      </Card>
    );
  }

  // The API refuses it anyway; saying so here means nobody types a
  // commission and presses a button to be told no.
  const blocked = partner.status !== 'APPROVED' && partner.kycStatus !== 'VERIFIED';

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('partners.decision')}</CardTitle>
          <CardDescription>{t('partners.decisionHint')}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {decide.error ? (
          <Alert variant="danger">
            {decide.error instanceof ZuruflexApiError
              ? decide.error.message
              : t('common:error.unexpected')}
          </Alert>
        ) : null}

        {blocked ? <Alert variant="warning">{t('partners.blockedByKyc')}</Alert> : null}

        {partner.status !== 'APPROVED' ? (
          <FormField label={t('common:field.commission')} hint={t('partners.commissionHint')}>
            {(field) => (
              <Input
                {...field}
                type="number"
                min={0}
                max={5000}
                value={commissionBps}
                onChange={(event) => setCommissionBps(event.target.value)}
              />
            )}
          </FormField>
        ) : null}

        <FormField label={t('partners.reason')} hint={t('partners.reasonHint')}>
          {(field) => (
            <Textarea
              {...field}
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </FormField>

        <div className="flex flex-wrap gap-2">
          {partner.status === 'APPROVED' ? (
            <Button
              variant="destructive"
              disabled={!reason.trim() || decide.isPending}
              onClick={() => decide.mutate('SUSPENDED')}
            >
              {t('partners.suspend')}
            </Button>
          ) : (
            <Button
              disabled={blocked || decide.isPending}
              onClick={() => decide.mutate('APPROVED')}
            >
              {t('partners.approve')}
            </Button>
          )}

          {partner.status !== 'CLOSED' ? (
            <Button
              variant="ghost"
              disabled={!reason.trim() || decide.isPending}
              onClick={() => decide.mutate('CLOSED')}
            >
              {t('partners.close')}
            </Button>
          ) : null}
        </div>

        {partner.status === 'APPROVED' ? (
          <p className="text-xs text-text-muted">{t('partners.suspendWarning')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
